import {
  parseCsvBooleanStrict,
} from '../../domain/csv/csvParseHelpers';
import type { CategoryRecord } from '../../domain/catalogTypes';
import { resolveMasterFromRecordFields, type MasterMatchCandidate } from '../../domain/csv/masterMatching';
import { listCategories, setCategoryActive } from '../categoriesRepo';
import {
  previewRow,
  type CsvPreviewRowOutcome,
  type ParsedRow,
} from './catalogCsvImportHelpers';

export type CategoryCsvPreviewContext = {
  categoryCandidates: MasterMatchCandidate[];
  categoryFullMap: Map<string, CategoryRecord>;
};

export async function loadCategoryCsvPreviewContext(): Promise<CategoryCsvPreviewContext> {
  const categories = await listCategories({ includeInactive: true });
  const categoryCandidates: MasterMatchCandidate[] = categories.map((c) => ({
    id: c.id,
    name: c.name,
    code: c.code ?? null,
    slug: c.slug,
  }));
  const categoryFullMap = new Map(categories.map((c) => [c.id, c]));
  return { categoryCandidates, categoryFullMap };
}

export function previewCategoryCsvRow(
  rowNumber: number,
  record: ParsedRow,
  ctx: CategoryCsvPreviewContext
): CsvPreviewRowOutcome {
  const nombre = record.nombre?.trim();
  if (!nombre) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', 'Sin nombre', 'Ingresá nombre.'),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const match = resolveMasterFromRecordFields(ctx.categoryCandidates, {
    id: record.categoria_id,
    label: nombre,
  });
  if (match.error || !match.id) {
    return {
      row: previewRow(
        rowNumber,
        'error',
        'validar',
        nombre,
        match.error ?? 'Esa categoría no está en el rubro. Activála en Categorías o pedila si falta.',
      ),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const existing = ctx.categoryFullMap.get(match.id);
  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) {
    return {
      row: previewRow(rowNumber, 'error', 'validar', nombre, activoCell.error),
      okCount: 0,
      skipCount: 0,
      errorCount: 1,
      newProductCount: 0,
    };
  }
  const isActive = activoCell.value;
  if (existing && isActive === undefined) {
    return {
      row: previewRow(rowNumber, 'skip', 'omitir', nombre, 'Sin cambio de activo.'),
      okCount: 0,
      skipCount: 1,
      errorCount: 0,
      newProductCount: 0,
    };
  }
  if (existing && isActive !== undefined && existing.isActive === isActive) {
    return {
      row: previewRow(rowNumber, 'skip', 'omitir', nombre),
      okCount: 0,
      skipCount: 1,
      errorCount: 0,
      newProductCount: 0,
    };
  }
  return {
    row: previewRow(rowNumber, 'ok', 'actualizar', nombre, 'Solo prende u oculta'),
    okCount: 1,
    skipCount: 0,
    errorCount: 0,
    newProductCount: 0,
  };
}

export async function commitCategoryRow(
  record: ParsedRow,
  categoryCandidates: MasterMatchCandidate[]
): Promise<'created' | 'updated'> {
  const nombre = record.nombre?.trim();
  if (!nombre) throw new Error('Nombre obligatorio.');

  const match = resolveMasterFromRecordFields(categoryCandidates, {
    id: record.categoria_id,
    label: nombre,
  });
  if (match.error || !match.id) {
    throw new Error(match.error ?? 'Esa categoría no está en el rubro.');
  }

  const activoCell = parseCsvBooleanStrict(record.activo);
  if (activoCell.error) throw new Error(activoCell.error);
  const isActive = activoCell.value;
  if (isActive === undefined) {
    throw new Error('En activo poné si o no. El CSV de categorías solo prende u oculta.');
  }

  await setCategoryActive(match.id, isActive);
  return 'updated';
}
